import React, { useEffect, useRef } from "react";
import axios from 'axios'
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

import Category from "./Category";
import Generator from "./Generator";
import CategoryNav from "./CategoryNav";
import IconDetail from "./IconDetail";
import SearchEmpty from "./SearchEmpty";
import { SectionSkeleton } from "./Skeleton";
import ControlPanel from "../Layouts/ControlPanel";
import PageBanner from "../Layouts/PageBanner";
import { iconLabel } from "../utils/svgIcon";
import { matchesStyle, styleCount } from "../utils/iconStyle";
import { searchIcons, SEARCH_LIMIT } from "../utils/searchIcons";
import {
  API_URL,
  ICON_COUNT_TEXT,
} from "../constants";

const STYLES = [
  { id: "all", label: "All" },
  { id: "line", label: "Line" },
  { id: "solid", label: "Solid" },
  { id: "duotone", label: "Duotone" },
];

// Visible tiles only: a duotone icon's second glyph has no tile of its own
const isTile = (icon) => icon.assign !== "after";

/**
 * Shorter searches that do find icons, for an empty result: each word of a multi-word
 * query, or the longest start (3+ letters) of a single word, e.g. "arrw" -> "arr".
 */
function partialSearches(icons, query) {
  const terms = query.toLowerCase().split(/[\s\-_]+/).filter(Boolean);
  const count = (term) => searchIcons(icons, term).filter(isTile).length;
  if (terms.length > 1) {
    return terms.map((term) => ({ term, count: count(term) })).filter((p) => p.count > 0);
  }
  for (let end = (terms[0] || "").length - 1; end >= 3; end--) {
    const term = terms[0].slice(0, end);
    const found = count(term);
    if (found) return [{ term, count: found }];
  }
  return [];
}


const Segmented = ({ label, options, value, onChange }) => (
  <div className="segmented" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        aria-pressed={value === option.id}
        title={option.title}
        onClick={() => onChange(option.id)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

/**
 * Placeholder for the next category. It asks for that category as soon as it nears the
 * viewport, so short pages that can't scroll still load the rest. Re-key it after each
 * load: a fresh observer reports right away if the placeholder is still in view.
 */
const LoadMoreSkeleton = ({ onVisible, listView }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          onVisible();
        }
      },
      { rootMargin: "350px 0px" }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref}>
      <SectionSkeleton count={14} listView={listView} />
    </div>
  );
};

LoadMoreSkeleton.propTypes = {
  onVisible: PropTypes.func.isRequired,
  listView: PropTypes.bool,
};

export const IconsPage = ({
  state,
  settings,
  localstore,
  onCategoryChange,
  onSearch,
  clearCatstore,
  setStyleFilter,
  loadMoreCategories,
  dispatch,
}) => {
  useEffect(() => {
    axios.get(API_URL + '/api/icons/all')
      .then(function (response) {
        let { result } = response.data

        dispatch({
          type: 'SET_ICONS',
          payload: result
        });
      })
      .catch(error => {
        console.error('ico error: ', error) // eslint-disable-line no-console
      });
  }, [])

  const styleFilter = state.styleFilter;
  // Categories with nothing in the chosen style are left out
  const visibleCategories = state.iconCategoryList.filter(
    (category) => styleCount(styleFilter, category) > 0
  );
  const searchMatches = state.searchSwitch ? state.searchIcons.filter(isTile) : [];
  const searchResults = searchMatches.filter((icon) => matchesStyle(styleFilter, icon));
  const shownResults = searchResults.slice(0, SEARCH_LIMIT);
  const styleLabel = (STYLES.find((style) => style.id === styleFilter) || STYLES[0]).label;
  const emptyStyle = (
    <div className="empty-icons">
      <h3>No {styleFilter} icons here</h3>
      <button type="button" className="btn btn-outline-primary" onClick={() => setStyleFilter("all")}>
        Show all styles
      </button>
    </div>
  );

  return (
    <div>
      <PageBanner
        slim
        title={
          <h1>
            {ICON_COUNT_TEXT} free, <span>meticulously crafted</span> icons
          </h1>
        }
      />
      <section className="icofont__icon__details">
        <Helmet>
          <title>{`Download ${ICON_COUNT_TEXT} Free, High-Quality Icons | IcoFont`}</title>
          <meta
            name="description"
            content={`Download from our extensive collection of ${ICON_COUNT_TEXT} free, high-quality icons including duotone. Ideal for enhancing your designs, websites, mobile apps, and software.`}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://icofont.com/" />
          <meta property="og:site_name" content="IcoFont" />
          <meta
            property="og:image"
            content="https://icofont.com/images/og/icons.png"
          />
          <meta name="og:title" content={`Download ${ICON_COUNT_TEXT} Free, High-Quality Icons | IcoFont`} />
          <meta
            name="og:description"
            content={`Download from our extensive collection of ${ICON_COUNT_TEXT} free, high-quality icons including duotone. Ideal for enhancing your designs, websites, mobile apps, and software.`}
          />
          <meta property="fb:app_id" content="101529056621542" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content="IcoFont" />
          <meta
            name="twitter:description"
            content={`Download from our extensive collection of ${ICON_COUNT_TEXT} free, high-quality icons including duotone. Ideal for enhancing your designs, websites, mobile apps, and software.`}
          />
          <meta
            name="twitter:image:src"
            content="https://icofont.com/images/og/icons.png"
          />
        </Helmet>
        <div className="icofont__details__wrap">
          <div className="large__container">
            <div className="icons-layout">
              <CategoryNav
                categories={state.categoryList}
                onToggle={onCategoryChange}
                onShowAll={clearCatstore}
              />
              <div className="icons-main">
                <ControlPanel state={state} onSearch={onSearch}>
                  <Segmented
                    label="Icon style"
                    options={STYLES}
                    value={styleFilter}
                    onChange={setStyleFilter}
                  />
                </ControlPanel>
                <div className="icon__list__2">
                  {state.searchSwitch ? (
                    searchResults.length > 0 ? (
                      <div>
                        <div className="icon-controller-box">
                          <div className="icon__on__selected">
                            <h2 className="is-query">Results for “{state.searchTxt.trim()}”</h2>
                            <span className="icon-count" role="status">
                              {searchResults.length > SEARCH_LIMIT
                                ? `Showing ${SEARCH_LIMIT} of ${searchResults.length} icons — add a word to narrow it down`
                                : `${searchResults.length} ${searchResults.length === 1 ? "icon" : "icons"}`}
                            </span>
                          </div>
                        </div>
                        <Generator entity={shownResults} />
                      </div>
                    ) : (
                      <SearchEmpty
                        query={state.searchTxt.trim()}
                        styleLabel={styleLabel}
                        hiddenByStyle={searchMatches.length}
                        partials={
                          searchMatches.length ? [] : partialSearches(localstore.icons, state.searchTxt)
                        }
                        onSearch={onSearch}
                        onShowAllStyles={() => setStyleFilter("all")}
                      />
                    )
                  ) : state.isIconReady ? (
                    visibleCategories.length === 0 ? (
                      emptyStyle
                    ) : (
                      visibleCategories.slice(0, state.limit).map((category) => (
                        <div key={category.cat_name}>
                          <div className="icon-controller-box">
                            <div className="icon__on__selected">
                              <h2>{iconLabel(category.cat_name)}</h2>
                              <span className="icon-count">
                                {styleCount(styleFilter, category)} icons
                              </span>
                            </div>
                          </div>
                          <Category entity={category} styleFilter={styleFilter} />
                        </div>
                      ))
                    )
                  ) : (
                    <SectionSkeleton
                      count={28}
                      listView={settings.isHorizontal}
                      label="Loading icons"
                    />
                  )}
                  {/* More categories load as you scroll; show where they will appear */}
                  {!state.searchSwitch &&
                  state.isIconReady &&
                  state.limit < visibleCategories.length ? (
                    <LoadMoreSkeleton
                      key={state.limit}
                      onVisible={loadMoreCategories}
                      listView={settings.isHorizontal}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <IconDetail />
    </div>
  );
};

IconsPage.propTypes = {
  state: PropTypes.object,
  localstore: PropTypes.object,
  selectedCategories: PropTypes.object,
  onCategoryChange: PropTypes.func,
  onSearch: PropTypes.func,
  clearCatstore: PropTypes.func,
  setStyleFilter: PropTypes.func,
  loadMoreCategories: PropTypes.func,
  settings: PropTypes.object,
};
export default IconsPage;
